import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import type { DotLottie } from '@lottiefiles/dotlottie-web';
import { OnboardingData, StudyPlan } from '../../types/onboarding';
import { generatePlan } from '../../services/onboardingPlan';
import OnboardingTrack from './OnboardingTrack';
import OnboardingCadence from './OnboardingCadence';
import OnboardingFocus from './OnboardingFocus';
import OnboardingExamDate from './OnboardingExamDate';
import PlanPreview from './PlanPreview';
import { X, ChevronLeft } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { getStudyState, saveStudyState } from '../../services/studyPlanStore';

const OnboardingWizard: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(true);

    // Lifted state for Step 3 (Focus) sub-steps
    const [subStep, setSubStep] = useState<'osp' | 'zsv'>('osp');

    // Initialize with default or loaded data
    const [data, setData] = useState<Partial<OnboardingData>>({
        planVersion: 1,
        lastUpdated: new Date().toISOString(),
        completedAt: new Date().toISOString(),
    });
    const [plan, setPlan] = useState<StudyPlan | null>(null);

    // Transition state management
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
    const [contentOpacity, setContentOpacity] = useState('opacity-100');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [lottieSource, setLottieSource] = useState('/materials/Success.lottie');
    const pendingDataRef = useRef<Partial<OnboardingData> | null>(null);
    const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);

    // Load initial data from Firestore (or migrate from local)
    useEffect(() => {
        const loadData = async () => {
            if (currentUser) {
                try {
                    const state = await getStudyState(currentUser.uid);
                    if (state && state.onboardingData) {
                        setData(state.onboardingData);
                        if (state.studyPlan) {
                            setPlan(state.studyPlan);
                        }
                    }
                } catch (e) {
                    console.error("Failed to load study state", e);
                }
            }
            setIsLoading(false);
        };
        loadData();
    }, [currentUser]);

    // Callback when Lottie animation completes
    const handleAnimationComplete = useCallback(async () => {
        if (!pendingDataRef.current) return;

        const newData = pendingDataRef.current;
        pendingDataRef.current = null;

        // Hide success animation
        setShowSuccessAnimation(false);

        // Remove undefined values from newData before merging
        const cleanedNewData = Object.fromEntries(
            Object.entries(newData).filter(([_, v]) => v !== undefined)
        );
        const updatedData = { ...data, ...cleanedNewData, completedAt: new Date().toISOString() };
        setData(updatedData);

        if (step === 4) {
            // Step 4 (Exam Date) -> Success Animation -> Finish
            // We use the data from the ref that triggered this animation
            if (newData) {
                finishOnboarding(newData);
            }
        } else {
            setStep(step + 1);
        }

        // Phase 3: Fade in new content
        setTimeout(() => {
            setContentOpacity('opacity-100');
            setIsTransitioning(false);
        }, 50); // Small delay to ensure DOM updates
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, step, currentUser]);

    // Listen for Lottie animation complete event
    useEffect(() => {
        if (!dotLottie) return;

        const handleComplete = () => {
            handleAnimationComplete();
        };

        dotLottie.addEventListener('complete', handleComplete);

        return () => {
            dotLottie.removeEventListener('complete', handleComplete);
        };
    }, [dotLottie, handleAnimationComplete]);

    const finishOnboarding = async (finalData: Partial<OnboardingData>) => {
        // Remove undefined values from newData before merging
        const cleanedNewData = Object.fromEntries(
            Object.entries(finalData).filter(([_, v]) => v !== undefined)
        );
        const updatedData = { ...data, ...cleanedNewData, completedAt: new Date().toISOString() };
        setData(updatedData);

        // Check if user has pro subscription (you'll need to implement this check)
        const isPro = false; // TODO: Replace with actual pro status check
        const { plan: finalPlan, requiresUpgrade } = generatePlan(updatedData as OnboardingData, isPro);

        // If upgrade required, store it to show modal after plan preview
        if (requiresUpgrade) {
            localStorage.setItem('pendingUpgradePrompt', JSON.stringify({
                topic: requiresUpgrade.topic,
                contentType: requiresUpgrade.contentType,
                fallbackTopic: requiresUpgrade.fallbackTopic,
                shown: false
            }));
        }

        // Clear daily completion flag to ensure new plan's mission is active
        const today = new Date().toISOString().split('T')[0];
        localStorage.removeItem(`mission_completed_${today}`);

        // Save to Firestore
        if (currentUser) {
            try {
                await saveStudyState(currentUser.uid, {
                    onboardingData: updatedData as OnboardingData,
                    studyPlan: finalPlan,
                    version: 1
                });
                console.log("Plan saved successfully");
                setPlan(finalPlan);
            } catch (e) {
                console.error("Failed to save plan:", e);
                alert("Nepodařilo se uložit plán. Zkus to prosím znovu.");
                return; // Stop if save fails
            }
        }

        // Fade out
        setIsTransitioning(true);
        setContentOpacity('opacity-0');

        // Navigate to dashboard after fade
        setTimeout(() => {
            // Clear the skipped flag since they completed onboarding
            localStorage.removeItem('onboardingSkipped');
            sessionStorage.setItem('justFinishedOnboarding', 'true');
            navigate('/', { state: { onboardingCompleted: true } });
        }, 300);
    };

    const handleNext = async (newData: Partial<OnboardingData>) => {
        if (isTransitioning) return;

        // Special handling for Step 3 (Focus) - Sub-steps for BOTH track
        if (step === 3 && data.track === 'BOTH' && subStep === 'osp') {
            // Merge data (weakness) but stay in Step 3, switch to ZSV
            setData(prev => ({ ...prev, ...newData }));

            // Trigger internal transition
            setIsTransitioning(true);
            setContentOpacity('opacity-0');
            setTimeout(() => {
                setSubStep('zsv');
                setIsTransitioning(false);
                setContentOpacity('opacity-100');
            }, 300);
            return;
        }

        // Store the new data for later processing
        pendingDataRef.current = newData;

        if (step === 4) {
            // Updated Step 4 Flow:
            // 1. Play Success Animation (The "Success Screen")
            // 2. On Complete -> finishOnboarding() -> Dashboard
            setShowSuccessAnimation(true);
        } else {
            // Phase 1: Fade out current content
            setIsTransitioning(true);
            setContentOpacity('opacity-0');

            // For steps 1-3: Just wait for the inline animation in the circle to complete
            setTimeout(() => {
                handleAnimationComplete();
            }, 400);
        }
    };

    const handleBack = () => {
        // Special handling for Step 3 Both track sub-steps
        if (step === 3 && data.track === 'BOTH' && subStep === 'zsv') {
            setIsTransitioning(true);
            setContentOpacity('opacity-0');
            setTimeout(() => {
                setSubStep('osp');
                setIsTransitioning(false);
                setContentOpacity('opacity-100');
            }, 300);
            return;
        }

        if (step > 1) {
            setStep(step - 1);
            // If going back to step 3 Both track, reset to OSP sub-step
            if (step - 1 === 3) setSubStep('osp');
        } else {
            navigate('/');
        }
    };

    const handleClose = () => {
        // Mark that user has skipped onboarding so they won't be redirected back
        localStorage.setItem('onboardingSkipped', 'true');
        navigate('/');
    };

    if (isLoading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-light-bg dark:bg-dark-bg p-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange"></div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-light-bg dark:bg-dark-bg p-2 sm:p-4 overflow-y-auto">
            <div className="relative w-full max-w-xl bg-white dark:bg-[#1a1d23] rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 my-4 sm:my-0 min-h-[500px]">
                {/* Header */}
                <div className="flex justify-between items-center mb-8 relative">
                    <div className="flex items-center gap-4">
                        {(step > 1 || (step === 3 && subStep === 'zsv')) && (
                            <button
                                onClick={handleBack}
                                className="p-2 -ml-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                            >
                                <ChevronLeft className="w-6 h-6 text-gray-500" />
                            </button>
                        )}
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white font-poppins">
                                {step === 4 ? 'Tvůj plán je připraven ✅' : 'Vytvoř si svůj plán'}
                            </h2>
                            {step < 5 && (
                                <p className="text-gray-500 dark:text-gray-400 text-sm">
                                    Krok {step} ze 4
                                </p>
                            )}
                        </div>
                    </div>
                    <button onClick={handleClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors absolute right-0 top-0">
                        <X className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                {/* Step Indicators */}
                {step < 5 && (
                    <div className="w-full mb-8">
                        <div className="flex items-center justify-between">
                            {[1, 2, 3, 4].map((stepNum) => {
                                const isCompleted = stepNum < step;
                                const isCurrent = stepNum === step;
                                const isAnimating = isCurrent && isTransitioning && !showSuccessAnimation;

                                return (
                                    <React.Fragment key={stepNum}>
                                        {/* Step Circle */}
                                        <div className="relative flex items-center justify-center">
                                            <div
                                                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isCompleted
                                                    ? 'bg-brand-orange text-white'
                                                    : isCurrent
                                                        ? 'border-2 border-brand-orange text-brand-orange'
                                                        : 'border-2 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                                                    } ${isAnimating ? 'animate-pulse scale-110' : ''}`}
                                            >
                                                {isCompleted ? (
                                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                ) : (
                                                    <span className="text-sm font-semibold">{stepNum}</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Connecting Line */}
                                        {stepNum < 4 && (
                                            <div className="flex-1 h-0.5 mx-2">
                                                <div
                                                    className={`h-full transition-all duration-300 ${stepNum < step ? 'bg-brand-orange' : 'bg-gray-300 dark:bg-gray-600'
                                                        }`}
                                                />
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Success Animation (Phase 2) */}
                {showSuccessAnimation && (
                    <div className="flex items-center justify-center h-64">
                        <DotLottieReact
                            src={lottieSource}
                            loop={false}
                            autoplay={true}
                            dotLottieRefCallback={setDotLottie}
                            className={lottieSource.includes('confetti') ? 'w-64 h-64' : 'w-48 h-48'}
                        />
                    </div>
                )}

                {/* Step Content with Transition */}
                {!showSuccessAnimation && (
                    <div className={`transition-opacity duration-300 ${contentOpacity}`}>
                        {step === 1 && (
                            <OnboardingTrack
                                onNext={handleNext}
                                initialData={data}
                                isTransitioning={isTransitioning}
                            />
                        )}
                        {step === 2 && (
                            <OnboardingCadence
                                onNext={handleNext}
                                onBack={handleBack}
                                initialData={data}
                                isTransitioning={isTransitioning}
                            />
                        )}
                        {step === 3 && (
                            <OnboardingFocus
                                onNext={handleNext}
                                onBack={handleBack}
                                initialData={data}
                                isTransitioning={isTransitioning}
                                subStep={subStep}
                            />
                        )}
                        {step === 4 && (
                            <OnboardingExamDate
                                onNext={handleNext}
                                onBack={handleBack}
                                initialData={data}
                                isTransitioning={isTransitioning}
                            />
                        )}
                        {step === 5 && plan && (
                            <PlanPreview plan={plan} />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OnboardingWizard;
